import React, { useCallback, useMemo } from "react";
import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/react";
import { Field, FieldProps, Formik } from "formik";
import { Flex, FormControl, FormLabel, Grid, Heading } from "@chakra-ui/react";
import AppListHeader from "@/features/ui/app-list-header";
import AppButton from "@/features/line-sheet-sets/app-button";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { Company, CompanyType, isFile, LightCompany, StoredObject } from "@/features/types";
import useCreditInvoiceDumbTransaction from "@/features/invoices/credit-invoices/hooks/use-credit-invoice-dumb-transaction";
import { DEFAULT_SEASON } from "@/features/line-sheet-sets/app-season-select";
import AppSeasonSelect from "@/features/line-sheet-sets/app-season-select";
import AppSelect from "@/features/line-sheet-sets/app-select";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import AppDatePicker from "@/features/line-sheet-sets/app-date-picker";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import AppTextArea from "@/features/ui/app-text-area";
import AppPriceInput, {
  PriceInputValue,
} from "@/features/invoices/app-price-input";
import InvoiceFileFormControl from "@/features/invoices/order-confirmation-invoices/invoice-file-form-control";
import { CreditInvoiceType } from "@/features/invoices/credit-invoices/credit-invoice.type";
import * as Yup from "yup";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import { toTitle } from "@/utils/case";
import PriceUtils from "@/features/ui/utils/price-utils";
import { ProformaInvoice } from "@/features/invoices/proforma-invoices/proforma-invoice.type";

interface AddCreditInvoiceFormFields {
  id: number;
  name: string;
  number: string;
  season?: string;
  type?: CreditInvoiceType;
  orderedBy: Pick<Company, "id" | "name">;
  issuedBy: Pick<Company, "id" | "name" | "type">;
  issuedThrough: LightCompany;
  issuedOn: Date;
  file?: File;
  publicComment?: string;
  privateComment?: string;
  amount?: PriceInputValue;
  orderConfirmationInvoices: { id: number }[];
  earningProformaInvoices: { id: number }[];
  usingProformaInvoices: { id: number }[];
  sendAsNewEmail: boolean;
  emailThreadMessageId?: string;
  ownedByCompanyType?: CompanyType;
  isUnoFeeDeductible: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  orderedBy: Pick<Company, "id" | "name">;
  issuedBy: Company;
  issuedThrough: Pick<Company, "id" | "name">;
  /** 모달 제목. 없으면 "크레딧 인보이스 업로드". */
  title?: string;
  /** 만들 크레딧의 타입. 화면에서 고르지 않고 여는 맥락이 정한다 — 오더 상세의 크레딧 발행은 FIXING,
   *  프로포마 상세의 크레딧 추가는 SEASON. 없으면 SEASON. */
  defaultType?: CreditInvoiceType;
  /** 하자(FIXING) 크레딧의 원인이 될 프로포르마 후보. 주면 타입이 FIXING 일 때 고르는 칸이 생기고,
   *  고른 프로포르마가 earning_proforma_invoices 로 연결된다(백엔드가 그 프로포마의 우노 수수료를 줄인다). */
  earningProformaInvoiceCandidates?: Pick<ProformaInvoice, "id" | "name">[];
  /** 생성 요청이 끝난 뒤(성공·실패 무관) 호출. 목록 갱신용. */
  onSuccess?: () => void;
}

export default function AddCreditInvoiceModal({
  isOpen,
  onClose,
  orderedBy,
  issuedBy,
  issuedThrough,
  title,
  defaultType = "SEASON",
  earningProformaInvoiceCandidates = [],
  onSuccess,
}: Props) {
  const identity = useIdentity();
  const hasEarningCandidates = earningProformaInvoiceCandidates.length > 0;
  const { t, tTitle } = useI18nHelper();
  const { error: showError } = useAppToasts();
  const { addCreate } = useCreditInvoiceDumbTransaction();
  const { withMessage } = useYupHelper({
    ns: "credit_invoices",
    prefix: "upload_credit_invoices_form",
  });

  const validationSchema = useMemo(
    () =>
      Yup.object({
        season: Yup.string().required(withMessage.required),
        type: Yup.mixed<CreditInvoiceType>().required(withMessage.required),
        issuedOn: Yup.date().required(withMessage.required),
        // 크레딧 사유는 바이어에게 보이는 공개 코멘트로 반드시 남긴다.
        publicComment: Yup.string().trim().required(withMessage.required),
        file: Yup.mixed<File | Pick<StoredObject, "id" | "name">>(),
        amount: Yup.object({
          value: Yup.number()
            .positive(withMessage.positive)
            .required(withMessage.required),
          currency: Yup.string().required(withMessage.required),
        }).required(withMessage.required),
        // 원인 프로포르마 후보를 받은 맥락(오더 상세)에서 하자 크레딧이면 최소 하나는 골라야 한다.
        earningProformaInvoices: Yup.array().test(
          "fixing-needs-earning-proforma",
          t(
            "credit_invoices:upload_credit_invoices_form.messages.error.either_order_confirm_invoice_or_proforma_invoice_if_type_is_fixing"
          ),
          function (value) {
            if (!hasEarningCandidates || this.parent.type !== "FIXING") {
              return true;
            }
            return (value?.length ?? 0) > 0;
          }
        ),
      }),
    [withMessage, t, hasEarningCandidates]
  );

  const validate = useCallback(
    async (values: AddCreditInvoiceFormFields) => {
      try {
        await validationSchema.validate(values, { abortEarly: false });
      } catch (e) {
        showError(e);
        return e;
      }
    },
    [validationSchema, showError]
  );

  const handleSubmit = useCallback(
    async (values: AddCreditInvoiceFormFields) => {
      if (identity?.company) {
        await addCreate(
          { by: identity.company.type, companyId: identity.company.id },
          {
            name: values.name || `${values.season} ${values.type}`,
            number: values.number || "",
            season: values.season!,
            type: values.type!,
            issuedOn: values.issuedOn,
            issuedByCompany: values.issuedBy,
            issuedToCompany: values.orderedBy,
            issuedThroughCompany: values.issuedThrough,
            file: values.file,
            amount: PriceUtils.placeholder(values.amount!, "EUR"),
            publicComment: values.publicComment || null,
            privateComment: values.privateComment || null,
            emailThreadMessageId: null,
            orderConfirmationInvoices: [],
            // 하자 크레딧만 원인 프로포르마를 연결한다. 시즌 크레딧은 원인이 없다.
            earningProformaInvoices:
              values.type === "FIXING"
                ? values.earningProformaInvoices.map((p) => ({ id: p.id }))
                : [],
            ownedByCompanyType: values.ownedByCompanyType || null,
            isUnoFeeDeductible:
              values.type === "FIXING" && values.isUnoFeeDeductible,
          }
        );
        onSuccess?.();
      }
      return new Promise((resolve) => setTimeout(() => resolve(true), 500));
    },
    [addCreate, identity, onSuccess]
  );

  const initialValues = useMemo(
    (): AddCreditInvoiceFormFields => ({
      id: -1,
      name: "",
      number: "",
      season: DEFAULT_SEASON,
      type: defaultType,
      orderedBy,
      issuedBy,
      issuedThrough: issuedThrough as LightCompany,
      issuedOn: (() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
      })(),
      orderConfirmationInvoices: [],
      // 후보가 하나뿐이면 미리 골라 둔다. 여럿이면 사용자가 고른다.
      earningProformaInvoices:
        earningProformaInvoiceCandidates.length === 1
          ? [{ id: earningProformaInvoiceCandidates[0].id }]
          : [],
      usingProformaInvoices: [],
      sendAsNewEmail: true,
      ownedByCompanyType: "BUYER",
      isUnoFeeDeductible: false,
    }),
    [orderedBy, issuedBy, issuedThrough, defaultType, earningProformaInvoiceCandidates]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius={"8px"} overflow={"hidden"} maxWidth={"800px"}>
        <ModalBody padding={"0"}>
          <Formik<AddCreditInvoiceFormFields>
            enableReinitialize={true}
            initialValues={initialValues}
            validate={validate}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Grid
                as={"form"}
                height={"100%"}
                width={"100%"}
                gridTemplateColumns={"1fr"}
                gridTemplateRows={"auto 1fr"}
              >
                <AppListHeader
                  headers={[
                    <Heading key={"header"} as={"h2"} fontSize={"14px"}>
                      {title ?? tTitle("credit_invoices:upload_credit_invoice")}
                    </Heading>,
                  ]}
                  filters={[]}
                  actions={[
                    <AppButton
                      key={"cancel"}
                      isDisabled={formik.isSubmitting}
                      onClick={onClose}
                    >
                      {tTitle("cancel")}
                    </AppButton>,
                    <AppButton
                      key={"upload"}
                      variant={"primary"}
                      isLoading={formik.isSubmitting}
                      onClick={async () => {
                        const result = await formik.submitForm();
                        // @ts-ignore
                        if (result) onClose();
                      }}
                    >
                      {tTitle("upload")}
                    </AppButton>,
                  ]}
                  showNavigateToBack={false}
                />
                <Grid
                  padding={"24px"}
                  gap={"12px"}
                  gridTemplateColumns={"1fr 1fr"}
                  overflow={"auto"}
                >
                  <Flex flexDirection={"column"} gap={"8px"}>
                    {/* 발행사 — 프로포르마의 부티크 또는 에이전시 자신. 에이전시 발행 크레딧은 그 에이전시를 통한
                        어느 부티크 오더에나 쓸 수 있다. */}
                    <Field name={"issuedBy"}>
                      {(props: FieldProps<AddCreditInvoiceFormFields["issuedBy"]>) => (
                        <FormControl
                          display={"flex"}
                          flexDirection={"column"}
                          gap={"4px"}
                          isRequired
                        >
                          <FormLabel
                            fontSize={"12px"}
                            color={"#6F6F6C"}
                            padding={"0"}
                            margin={"0"}
                          >
                            {toTitle(t("issued_by"))}
                          </FormLabel>
                          <AppSelect<number>
                            name={toTitle(t("issued_by"))}
                            width={"100%"}
                            value={props.field.value?.id}
                            options={[
                              { name: `${issuedBy.name} (${toTitle(t("boutique"))})`, value: issuedBy.id },
                              { name: `${issuedThrough.name} (${toTitle(t("agency"))})`, value: issuedThrough.id },
                            ]}
                            onSelect={(value) =>
                              props.form.setFieldValue(
                                props.field.name,
                                value === issuedThrough.id
                                  ? { id: issuedThrough.id, name: issuedThrough.name, type: "AGENCY" as CompanyType }
                                  : issuedBy
                              )
                            }
                          />
                        </FormControl>
                      )}
                    </Field>

                    <Field name={"season"}>
                      {(props: FieldProps<string>) => (
                        <FormControl
                          display={"flex"}
                          flexDirection={"column"}
                          gap={"4px"}
                          isRequired
                        >
                          <FormLabel
                            fontSize={"12px"}
                            color={"#6F6F6C"}
                            padding={"0"}
                            margin={"0"}
                          >
                            {toTitle(t("season"))}
                          </FormLabel>
                          <AppSeasonSelect
                            width={"100%"}
                            value={props.field.value}
                            onSelect={(value) =>
                              props.form.setFieldValue(props.field.name, value)
                            }
                          />
                        </FormControl>
                      )}
                    </Field>

                    {/* 하자 크레딧의 원인 프로포르마 — 오더 상세에서 열었을 때만(후보가 있을 때만) 보인다. */}
                    {hasEarningCandidates && formik.values.type === "FIXING" && (
                      <Field name={"earningProformaInvoices"}>
                        {(props: FieldProps<AddCreditInvoiceFormFields["earningProformaInvoices"]>) => (
                          <FormControl
                            display={"flex"}
                            flexDirection={"column"}
                            gap={"4px"}
                            isRequired
                          >
                            <FormLabel
                              fontSize={"12px"}
                              color={"#6F6F6C"}
                              padding={"0"}
                              margin={"0"}
                            >
                              {toTitle(t("earning_proforma_invoices"))}
                            </FormLabel>
                            <AppSelect<number>
                              name={toTitle(t("earning_proforma_invoices"))}
                              width={"100%"}
                              isMultiple={true}
                              isSearchable={true}
                              value={props.field.value.map((p) => p.id)}
                              options={earningProformaInvoiceCandidates.map((p) => ({
                                name: p.name,
                                value: p.id,
                              }))}
                              onSelect={(value) =>
                                props.form.setFieldValue(
                                  props.field.name,
                                  (value ?? []).map((id) => ({ id }))
                                )
                              }
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}

                    {/* 하자 크레딧만: 원인 프로포르마의 우노 수수료 계산에서 이 금액을 뺄지. */}
                    {formik.values.type === "FIXING" && (
                      <Field name={"isUnoFeeDeductible"}>
                        {(props: FieldProps<boolean>) => (
                          <FormControl
                            display={"flex"}
                            flexDirection={"column"}
                            gap={"4px"}
                          >
                            <AppCheckBox
                              isChecked={!!props.field.value}
                              name={toTitle(t("uno_fee_deductible"))}
                              onChange={(value) =>
                                props.form.setFieldValue(props.field.name, value)
                              }
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}

                    <Field name={"ownedByCompanyType"}>
                      {(props: FieldProps<CompanyType>) => (
                        <FormControl
                          display={"flex"}
                          flexDirection={"column"}
                          gap={"4px"}
                        >
                          <FormLabel
                            fontSize={"12px"}
                            color={"#6F6F6C"}
                            padding={"0"}
                            margin={"0"}
                          >
                            {toTitle(t("owned_by_company_type"))}
                          </FormLabel>
                          <AppSelect
                            name={toTitle(t("owned_by_company_type"))}
                            width={"100%"}
                            value={props.field.value}
                            options={[
                              { name: toTitle(t("agency")), value: "AGENCY" as CompanyType },
                              { name: toTitle(t("buyer")), value: "BUYER" as CompanyType },
                            ]}
                            onSelect={(value) => {
                              props.form.setFieldValue(props.field.name, value);
                              // 바이어 소유로 돌아가면 수입사 선택란이 사라지므로 값도 인보이스의 바이어로 되돌린다.
                              if (value !== "AGENCY") {
                                props.form.setFieldValue("orderedBy", orderedBy);
                              }
                            }}
                            isClearable={true}
                          />
                        </FormControl>
                      )}
                    </Field>

                    {/* 에이전시 소유 크레딧만 다른 바이어 앞으로 만들 수 있다. 기본값은 프로포마의 바이어. */}
                    {formik.values.ownedByCompanyType === "AGENCY" && (
                      <Field name={"orderedBy"}>
                        {(props: FieldProps<AddCreditInvoiceFormFields["orderedBy"]>) => (
                          <FormControl
                            display={"flex"}
                            flexDirection={"column"}
                            gap={"4px"}
                            isRequired
                          >
                            <FormLabel
                              fontSize={"12px"}
                              color={"#6F6F6C"}
                              padding={"0"}
                              margin={"0"}
                            >
                              {toTitle(t("ordered_by"))}
                            </FormLabel>
                            <AppCompanySelect
                              name={tTitle("ordered_by")}
                              types={["BUYER"]}
                              width={"100%"}
                              id={props.field.value?.id}
                              onSelect={(value) =>
                                props.form.setFieldValue(
                                  props.field.name,
                                  value ? { id: value.id, name: value.name } : orderedBy
                                )
                              }
                            />
                          </FormControl>
                        )}
                      </Field>
                    )}

                    <Field name={"issuedOn"}>
                      {(props: FieldProps<Date>) => (
                        <FormControl
                          display={"flex"}
                          flexDirection={"column"}
                          gap={"4px"}
                          isRequired
                        >
                          <FormLabel
                            fontSize={"12px"}
                            color={"#6F6F6C"}
                            padding={"0"}
                            margin={"0"}
                          >
                            {toTitle(t("issued_on"))}
                          </FormLabel>
                          <AppDatePicker
                            width={"100%"}
                            showTimeInput={false}
                            name={tTitle("issued_on")}
                            value={props.field.value}
                            onChange={(value) =>
                              props.form.setFieldValue("issuedOn", value)
                            }
                          />
                        </FormControl>
                      )}
                    </Field>

                    <Field name={"publicComment"}>
                      {(props: FieldProps<string>) => (
                        <FormControl
                          display={"flex"}
                          flexDirection={"column"}
                          gap={"4px"}
                          isRequired
                        >
                          <FormLabel
                            fontSize={"12px"}
                            color={"#6F6F6C"}
                            padding={"0"}
                            margin={"0"}
                          >
                            {toTitle(t("credit_issue_reason"))}
                          </FormLabel>
                          <AppTextArea
                            fontSize={"12px"}
                            rows={3}
                            value={props.field.value || ""}
                            onChange={(e) =>
                              props.form.setFieldValue(
                                props.field.name,
                                e.target.value
                              )
                            }
                          />
                        </FormControl>
                      )}
                    </Field>

                    <Field name={"privateComment"}>
                      {(props: FieldProps<string>) => (
                        <FormControl
                          display={"flex"}
                          flexDirection={"column"}
                          gap={"4px"}
                        >
                          <FormLabel
                            fontSize={"12px"}
                            color={"#6F6F6C"}
                            padding={"0"}
                            margin={"0"}
                          >
                            {toTitle(t("private_comment"))}
                          </FormLabel>
                          <AppTextArea
                            fontSize={"12px"}
                            rows={3}
                            value={props.field.value || ""}
                            onChange={(e) =>
                              props.form.setFieldValue(
                                props.field.name,
                                e.target.value
                              )
                            }
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Flex>

                  <Flex flexDirection={"column"} gap={"8px"}>
                    <InvoiceFileFormControl formik={formik} isEditing={true} />

                    <Field name={"amount"}>
                      {(props: FieldProps<PriceInputValue>) => (
                        <FormControl
                          display={"flex"}
                          flexDirection={"column"}
                          gap={"4px"}
                          isRequired
                        >
                          <FormLabel
                            fontSize={"12px"}
                            color={"#6F6F6C"}
                            padding={"0"}
                            margin={"0"}
                          >
                            {tTitle("amount")}
                          </FormLabel>
                          <AppPriceInput
                            value={props.field.value}
                            onChange={(value) =>
                              props.form.setFieldValue(props.field.name, value)
                            }
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Flex>
                </Grid>
              </Grid>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
